import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { toast } from "sonner";

/**
 * Custom React hook that gives the app proper Android hardware back-button behaviour 
 * when running inside the Capacitor native shell.
 */
export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  const lastBackPressRef = useRef<number>(0);

  useEffect(() => {
    // Completely inert on normal web browsers
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let isCancelled = false;
    let listenerHandle: PluginListenerHandle | null = null;

    const handleBackButton = () => {
      // 1. Close any open overlay first.
      // Query the DOM for open Radix overlays like dialogs, alert dialogs, menus, and listboxes.
      const openOverlay = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
      );

      if (openOverlay) {
        // Radix's dismiss layer listens for 'Escape' keydown to close overlays.
        document.dispatchEvent(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true })
        );
        return;
      }

      // 2. Navigate back if there is history to go back to.
      // React Router v6 stores its history index on window.history.state.idx.
      const historyState = window.history.state;
      const idx = typeof historyState?.idx === "number" ? historyState.idx : 0;
      
      if (idx > 0) {
        navigate(-1);
        return;
      }

      // 3. Otherwise offer double-press-to-exit.
      const now = Date.now();
      if (now - lastBackPressRef.current < 2000) {
        App.exitApp();
      } else {
        lastBackPressRef.current = now;
        toast("Press back again to exit");
      }
    };

    // Register the listener via App.addListener
    App.addListener("backButton", handleBackButton)
      .then((handle) => {
        if (isCancelled) {
          // If the effect was cleaned up before the promise resolved, remove it immediately.
          handle.remove();
        } else {
          listenerHandle = handle;
        }
      })
      .catch((err) => {
        console.error("Failed to add backButton listener", err);
      });

    // Cleanup
    return () => {
      isCancelled = true;
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate]); // Only re-run if navigate changes
}